import { createAsyncThunk, createSlice } from '@reduxjs/toolkit';
import { Metadata } from '../../services/Contexture/Metadata';

const CACHE_TTL_MS = 2 * 60 * 60 * 1000;
const STORAGE_KEY = 'contextureMetadataCache';

const loadFromStorage = () => {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw);
    if (!parsed || typeof parsed !== 'object') return null;
    return parsed;
  } catch {
    return null;
  }
};

const saveToStorage = (state) => {
  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(state));
  } catch {
    // Silently ignore write failures
  }
};

const pruneExpiredEntries = (repos) => {
  const now = Date.now();
  const pruned = {};
  Object.entries(repos || {}).forEach(([repoId, entry]) => {
    if (entry?.fetchedAt && now - entry.fetchedAt < CACHE_TTL_MS) {
      pruned[repoId] = entry;
    }
  });
  return pruned;
};

const persisted = loadFromStorage();
const persistedRepos = pruneExpiredEntries(persisted?.repos || {});

const initialState = {
  repos: persistedRepos,
  allUniqueFields: persisted?.allUniqueFields || [],
  isFetching: false,
  error: null,
};

const isRepoCacheValid = (repoEntry) => {
  if (!repoEntry?.fetchedAt) return false;
  return Date.now() - repoEntry.fetchedAt < CACHE_TTL_MS;
};

const computeAllUniqueFields = (repos) => {
  const byFieldName = new Map();

  Object.values(repos || {}).forEach((repoEntry) => {
    (repoEntry?.fields || []).forEach((field) => {
      const fieldName = field?.fieldName;
      if (!fieldName) return;

      if (!byFieldName.has(fieldName)) {
        byFieldName.set(fieldName, field);
        return;
      }

      const existing = byFieldName.get(fieldName);
      const existingHasLabel = !!existing?.label;
      const incomingHasLabel = !!field?.label;
      if (!existingHasLabel && incomingHasLabel) {
        byFieldName.set(fieldName, field);
      }
    });
  });

  return Array.from(byFieldName.values());
};


const computeSearchLayoutFieldNames = (objectTypes) => {
  const objectTypesWithLayouts = (objectTypes || []).filter(
    (ot) => Array.isArray(ot?.searchLayouts) && ot.searchLayouts.length > 0
  );

  if (objectTypesWithLayouts.length === 0) return [];

  let intersection = null;

  objectTypesWithLayouts.forEach((ot) => {
    const namesForType = new Set(
      (ot.searchLayouts || [])
        .map((layout) => layout?.fieldName)
        .filter(Boolean)
    );

    if (intersection === null) {
      intersection = namesForType;
      return;
    }

    intersection = new Set(
      Array.from(intersection).filter((fieldName) => namesForType.has(fieldName))
    );
  });

  return Array.from(intersection || []);
};

const computeSearchLayoutFields = (fields, searchLayoutFieldNames) => {
  const byFieldName = new Map();
  (fields || []).forEach((f) => {
    if (f?.fieldName) byFieldName.set(f.fieldName, f);
  });

  return (searchLayoutFieldNames || [])
    .map((fieldName) => byFieldName.get(fieldName))
    .filter(Boolean);
};

export const ensureContextureMetadataForRepos = createAsyncThunk(
  'contextureMetadata/ensureForRepos',
  async (repoIds, { getState, rejectWithValue }) => {
    try {
      const state = getState();
      const existingRepos = state.contextureMetadata?.repos || {};

      const uniqueRepoIds = Array.from(new Set((repoIds || []).filter(Boolean)));
      const missingOrExpired = uniqueRepoIds.filter((repoId) => {
        const entry = existingRepos?.[repoId];
        return !isRepoCacheValid(entry);
      });

      if (missingOrExpired.length === 0) {
        return { repos: {} };
      }

      // Process requests sequentially to prevent CORS issues from multiple simultaneous calls
      const results = [];
      for (const repoId of missingOrExpired) {
        try {
          // Add small delay between repo requests to prevent overwhelming the server
          if (results.length > 0) {
            await new Promise(resolve => setTimeout(resolve, 100));
          }
          
          const [fieldsRes, objectTypesRes] = await Promise.all([
            Metadata.fetchFields(repoId),
            Metadata.fetchObjectTypes(repoId),
          ]);

          const fields = fieldsRes?.fields || [];
          const objectTypes = objectTypesRes || [];
          const searchLayoutFieldNames = computeSearchLayoutFieldNames(objectTypes);

          results.push({
            repoId,
            fields,
            objectTypes,
            searchLayoutFieldNames,
            searchLayoutFields: computeSearchLayoutFields(fields, searchLayoutFieldNames),
            fetchedAt: Date.now(),
          });
        } catch (error) {
          console.error(`Failed to fetch metadata for repo ${repoId}:`, error);
        }
      }

      const reposPayload = results.reduce((acc, item) => {
        acc[item.repoId] = {
          fields: item.fields,
          objectTypes: item.objectTypes,
          searchLayoutFieldNames: item.searchLayoutFieldNames,
          searchLayoutFields: item.searchLayoutFields,
          fetchedAt: item.fetchedAt,
        };
        return acc;
      }, {});

      return { repos: reposPayload };
    } catch (error) {
      return rejectWithValue(error.message || 'Failed to fetch contexture metadata');
    }
  }
);

const contextureMetadataSlice = createSlice({
  name: 'contextureMetadata',
  initialState,
  reducers: {
    clearContextureMetadataState(state) {
      Object.assign(state, initialState);
      try {
        localStorage.removeItem(STORAGE_KEY);
      } catch {}
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(ensureContextureMetadataForRepos.pending, (state) => {
        state.isFetching = true;
        state.error = null;
      })
      .addCase(ensureContextureMetadataForRepos.fulfilled, (state, action) => {
        state.isFetching = false;
        state.error = null;

        const incomingRepos = action.payload?.repos || {};
        state.repos = {
          ...(state.repos || {}),
          ...incomingRepos,
        };

        state.allUniqueFields = computeAllUniqueFields(state.repos);
        saveToStorage(state);
      })
      .addCase(ensureContextureMetadataForRepos.rejected, (state, action) => {
        state.isFetching = false;
        state.error = action.payload || action.error.message;
      });
  },
});

export const { clearContextureMetadataState } = contextureMetadataSlice.actions;

export default contextureMetadataSlice.reducer;
